import React, { useState, useEffect } from 'react';
import {
  FaChartLine,
  FaUsers,
  FaShoppingBag,
  FaBox,
  FaMoneyBillWave,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import LoadingSpinner from '../LoadingSpinner';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    revenue: 0,
    orders: 0,
    customers: 0,
    products: 0,
  });
  const [timeRange, setTimeRange] = useState('week');
  const [revenueData, setRevenueData] = useState({});
  const [salesByCategory, setSalesByCategory] = useState({});
  const [topProducts, setTopProducts] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);

        // Fetch summary data
        const summaryResponse = await fetch(`/api/admin/analytics/summary?range=${timeRange}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!summaryResponse.ok) {
          throw new Error('Failed to fetch summary data');
        }

        const summaryData = await summaryResponse.json();
        setSummary(summaryData);

        // Fetch revenue chart data
        const revenueResponse = await fetch(`/api/admin/analytics/revenue?range=${timeRange}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!revenueResponse.ok) {
          throw new Error('Failed to fetch revenue data');
        }

        const revenueChartData = await revenueResponse.json();
        setRevenueData(revenueChartData);

        // Fetch sales by category
        const categoryResponse = await fetch(
          `/api/admin/analytics/sales-by-category?range=${timeRange}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        );

        if (!categoryResponse.ok) {
          throw new Error('Failed to fetch category data');
        }

        const categoryData = await categoryResponse.json();
        setSalesByCategory(categoryData);

        // Fetch top products
        const productsResponse = await fetch(
          `/api/admin/analytics/top-products?range=${timeRange}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        );

        if (!productsResponse.ok) {
          throw new Error('Failed to fetch product data');
        }

        const productsData = await productsResponse.json();
        setTopProducts(productsData);

        // Fetch low stock items
        const stockResponse = await fetch('/api/admin/analytics/low-stock', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!stockResponse.ok) {
          throw new Error('Failed to fetch stock data');
        }

        const stockData = await stockResponse.json();
        setLowStockItems(stockData);
      } catch (error) {
        // Error will be handled by the global error handler
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [timeRange]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // Prepare chart data
  const revenueChartData = {
    labels: revenueData.labels || [],
    datasets: [
      {
        label: 'Revenue',
        data: revenueData.revenue || [],
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Orders',
        data: revenueData.orders || [],
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        tension: 0.4,
      },
    ],
  };

  // Category data for doughnut chart
  const categoryChartData = {
    labels: salesByCategory.labels || [],
    datasets: [
      {
        data: salesByCategory.data || [],
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Top products data for bar chart
  const topProductsChartData = {
    labels: topProducts.map(product => product.name) || [],
    datasets: [
      {
        label: 'Units Sold',
        data: topProducts.map(product => product.quantity) || [],
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
      },
    ],
  };

  return (
    <div className='analytics-dashboard'>
      <div className='time-range-selector'>
        <button
          className={`range-btn ${timeRange === 'week' ? 'active' : ''}`}
          onClick={() => setTimeRange('week')}
        >
          Last 7 Days
        </button>
        <button
          className={`range-btn ${timeRange === 'month' ? 'active' : ''}`}
          onClick={() => setTimeRange('month')}
        >
          Last 30 Days
        </button>
        <button
          className={`range-btn ${timeRange === 'year' ? 'active' : ''}`}
          onClick={() => setTimeRange('year')}
        >
          Last 12 Months
        </button>
      </div>

      <div className='stats-cards'>
        <div className='stat-card'>
          <div className='stat-icon revenue'>
            <FaMoneyBillWave />
          </div>
          <div className='stat-content'>
            <h3>Total Revenue</h3>
            <p className='stat-value'>${summary.revenue.toFixed(2)}</p>
            <p className={`stat-change ${summary.revenueChange >= 0 ? 'positive' : 'negative'}`}>
              {summary.revenueChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
              {Math.abs(summary.revenueChange)}% from previous period
            </p>
          </div>
        </div>

        <div className='stat-card'>
          <div className='stat-icon orders'>
            <FaShoppingBag />
          </div>
          <div className='stat-content'>
            <h3>Orders</h3>
            <p className='stat-value'>{summary.orders}</p>
            <p className={`stat-change ${summary.ordersChange >= 0 ? 'positive' : 'negative'}`}>
              {summary.ordersChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
              {Math.abs(summary.ordersChange)}% from previous period
            </p>
          </div>
        </div>

        <div className='stat-card'>
          <div className='stat-icon customers'>
            <FaUsers />
          </div>
          <div className='stat-content'>
            <h3>Customers</h3>
            <p className='stat-value'>{summary.customers}</p>
            <p className={`stat-change ${summary.customersChange >= 0 ? 'positive' : 'negative'}`}>
              {summary.customersChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
              {Math.abs(summary.customersChange)}% from previous period
            </p>
          </div>
        </div>

        <div className='stat-card'>
          <div className='stat-icon products'>
            <FaBox />
          </div>
          <div className='stat-content'>
            <h3>Products</h3>
            <p className='stat-value'>{summary.products}</p>
            <p className='stat-note'>{lowStockItems.length} items low in stock</p>
          </div>
        </div>
      </div>

      <div className='chart-grid'>
        <div className='chart-container revenue-chart'>
          <h3>Revenue & Orders Trend</h3>
          <Line data={revenueChartData} options={{ maintainAspectRatio: false }} />
        </div>

        <div className='chart-container category-chart'>
          <h3>Sales by Category</h3>
          <Doughnut data={categoryChartData} options={{ maintainAspectRatio: false }} />
        </div>

        <div className='chart-container products-chart'>
          <h3>Top Selling Products</h3>
          <Bar
            data={topProductsChartData}
            options={{
              maintainAspectRatio: false,
              indexAxis: 'y',
            }}
          />
        </div>

        <div className='inventory-alert'>
          <h3>
            <FaExclamationTriangle /> Low Stock Alert
          </h3>
          {lowStockItems.length === 0 ? (
            <p>All products have sufficient inventory.</p>
          ) : (
            <ul className='low-stock-list'>
              {lowStockItems.map(item => (
                <li key={item.id} className='low-stock-item'>
                  <div className='item-info'>
                    <img src={item.image_url} alt={item.name} />
                    <div>
                      <h4>{item.name}</h4>
                      <p>{item.category}</p>
                    </div>
                  </div>
                  <div className='stock-count'>
                    <span>{item.stock} left</span>
                    <button className='btn-restock'>Restock</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
